import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';

import { ProductTourService } from '../../services/product-tour.service';
import { TranslatePipe } from '../../pipes/translate/translate.pipe';

/**
 * ProductTourComponent component.
 * Provides UI presentation and event handling for product tour component.
 */
@Component({
  selector: 'app-product-tour',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './product-tour.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./product-tour.component.scss'],
})
export class ProductTourComponent implements OnDestroy {
  /** Injected service for service. */
  private service = inject(ProductTourService);

  /**
   * Reference to the tooltip el element or component.
   */
  @ViewChild('tooltip') tooltipEl!: ElementRef;

  /** Property is visible. */
  isVisible = this.service.active;
  /** Property current index. */
  currentIndex = this.service.index;
  /** Property total steps. */
  totalSteps = this.service.totalSteps;
  /** Property current step. */
  currentStep = this.service.currentStep;

  /** Property scroll cleanups. */
  private scrollCleanups: (() => void)[] = [];

  /** Property highlight rect. */
  highlightRect = signal<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  /** Property tooltip pos. */
  tooltipPos = signal<{ top: number; left: number } | null>(null);

  /** Property clip path. */
  clipPath = computed(() => {
    const rect = this.highlightRect();
    if (!rect) return 'polygon(0% 0%, 0% 100%, 100% 100%, 100% 0%)';

    const { top, left, width, height } = rect;
    const padding = 8;
    const t = top - padding;
    const l = left - padding;
    const w = width + padding * 2;
    const h = height + padding * 2;
    const r = l + w;
    const b = t + h;

    // This creates a polygon "frame" with a hole in the middle
    return `polygon(
      0% 0%, 
      0% 100%, 
      ${l}px 100%, 
      ${l}px ${t}px, 
      ${r}px ${t}px, 
      ${r}px ${b}px, 
      ${l}px ${b}px, 
      ${l}px 100%, 
      100% 100%, 
      100% 0%
    )`;
  });

  constructor() {
    // When the step changes, scroll target into view then recalculate positions
    effect(() => {
      const step = this.currentStep();
      const active = this.isVisible();
      if (active && step) {
        setTimeout(() => this.scrollAndUpdatePositions(), 100);
      } else {
        this.detachScrollListeners();
      }
    });
  }

  /**
   * Lifecycle hook that cleans up subscriptions on component destruction.
   */
  ngOnDestroy() {
    this.detachScrollListeners();
  }

  /**
   * Handles on resize logic.
   */
  @HostListener('window:resize')
  onResize() {
    if (this.isVisible()) {
      this.updatePositions();
    }
  }

  /**
   * Handles scroll and update positions logic.
   */
  private scrollAndUpdatePositions() {
    const step = this.currentStep();
    if (!step) return;

    const element = document.querySelector(step.selector);
    if (!element) {
      this.detachScrollListeners();
      this.updatePositions();
      return;
    }

    this.scrollTargetIntoView(element);
    this.attachScrollListeners(element);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.updatePositions());
    });
  }

  /**
   * Handles scroll target into view logic.
   * @param element - The element
   */
  private scrollTargetIntoView(element: Element) {
    (element as HTMLElement).scrollIntoView({
      block: 'center',
      inline: 'nearest',
    });
  }

  /**
   * Handles attach scroll listeners logic.
   * @param element - The element
   */
  private attachScrollListeners(element: Element) {
    this.detachScrollListeners();

    const handler = () => this.updatePositions();

    window.addEventListener('scroll', handler, { passive: true });
    this.scrollCleanups.push(() =>
      window.removeEventListener('scroll', handler)
    );

    let parent = element.parentElement;
    while (parent) {
      const style = getComputedStyle(parent);
      const isScrollable =
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll' ||
        style.overflow === 'auto' ||
        style.overflow === 'scroll';

      if (isScrollable && parent.scrollHeight > parent.clientHeight) {
        parent.addEventListener('scroll', handler, { passive: true });
        const scrollParent = parent;
        this.scrollCleanups.push(() =>
          scrollParent.removeEventListener('scroll', handler)
        );
      }

      parent = parent.parentElement;
    }
  }

  /**
   * Handles detach scroll listeners logic.
   */
  private detachScrollListeners() {
    this.scrollCleanups.forEach((cleanup) => cleanup());
    this.scrollCleanups = [];
  }

  /**
   * Handles update positions logic.
   */
  private updatePositions() {
    const step = this.currentStep();
    if (!step) return;

    const element = document.querySelector(step.selector);
    if (!element) {
      console.warn(`Product Tour: Element ${step.selector} not found`);
      this.highlightRect.set(null);
      this.tooltipPos.set({
        top: window.innerHeight / 2 - 100,
        left: window.innerWidth / 2 - 150,
      });
      return;
    }

    const rect = element.getBoundingClientRect();
    this.highlightRect.set({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });

    this.calculateTooltipPosition(rect, step.position || 'bottom', step.offset);
  }

  /**
   * Handles calculate tooltip position logic.
   * @param targetRect - The target rect
   * @param position - The position
   * @param offset - The offset
   */
  private calculateTooltipPosition(
    targetRect: DOMRect,
    position: string,
    offset = { x: 0, y: 0 }
  ) {
    const padding = 20; // Distance from highlight
    const highlightPadding = 8;

    // Initial best guess - will be refined by actual tooltip size if possible
    let top = 0;
    let left = 0;

    const t = targetRect.top - highlightPadding;
    const l = targetRect.left - highlightPadding;
    const w = targetRect.width + highlightPadding * 2;
    const h = targetRect.height + highlightPadding * 2;

    switch (position) {
      case 'top':
        top =
          t - (this.tooltipEl?.nativeElement?.offsetHeight || 150) - padding;
        left = l;
        break;
      case 'bottom':
        top = t + h + padding;
        left = l;
        break;
      case 'left':
        top = t;
        left =
          l - (this.tooltipEl?.nativeElement?.offsetWidth || 280) - padding;
        break;
      case 'right':
        top = t;
        left = l + w + padding;
        break;
      case 'center':
        top = window.innerHeight / 2 - 100;
        left = window.innerWidth / 2 - 140;
        break;
      default:
        top = t + h + padding;
        left = l;
    }

    // Apply offset
    top += offset.y || 0;
    left += offset.x || 0;

    // Keep within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tooltipWidth = this.tooltipEl?.nativeElement?.offsetWidth || 280;
    const tooltipHeight = this.tooltipEl?.nativeElement?.offsetHeight || 150;

    if (left < 10) left = 10;
    if (left + tooltipWidth > viewportWidth - 10)
      left = viewportWidth - tooltipWidth - 10;
    if (top < 10) top = 10;
    if (top + tooltipHeight > viewportHeight - 10)
      top = viewportHeight - tooltipHeight - 10;

    this.tooltipPos.set({ top, left });
  }

  /**
   * Handles next logic.
   */
  next() {
    this.service.next();
  }

  /**
   * Handles previous logic.
   */
  previous() {
    this.service.previous();
  }

  /**
   * Handles skip logic.
   */
  skip() {
    this.service.skip();
  }
}
